/**
 * Hook de tiempo real (§12): SSE por `fetch` (para poder mandar el header Authorization)
 * con reconexión y `Last-Event-ID`, y fallback automático a polling cada 5 s.
 *
 * Se descartan los eventos con `seq` menor al último visto: ante un reordenamiento de red,
 * todas las terminales convergen al mismo estado.
 */
import { useEffect, useRef, useState } from 'react';
import { API_URL, getAccessToken } from './api.js';
import type { StreamEvent } from '@thepit/contracts';

export type StreamStatus = 'connecting' | 'live' | 'polling' | 'offline';

export interface UseStreamOptions {
  fightId: string | null;
  onEvent: (event: StreamEvent) => void;
  /** Se llama cuando hay que re-sincronizar por polling. */
  onPoll: () => void;
  enabled?: boolean;
}

const MAX_SSE_FAILURES = 3;

export function useFightStream({ fightId, onEvent, onPoll, enabled = true }: UseStreamOptions): {
  status: StreamStatus;
  lastSeq: number;
} {
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const lastSeqRef = useRef(0);
  const handlerRef = useRef(onEvent);
  const pollRef = useRef(onPoll);
  handlerRef.current = onEvent;
  pollRef.current = onPoll;

  useEffect(() => {
    if (!enabled || !fightId) {
      setStatus('offline');
      return undefined;
    }

    let disposed = false;
    let controller: AbortController | null = null;
    let failures = 0;
    let pollTimer: number | undefined;
    let reconnectTimer: number | undefined;

    const startPolling = (): void => {
      if (disposed) return;
      setStatus('polling');
      pollRef.current();
      pollTimer = window.setInterval(() => pollRef.current(), 5_000);
    };

    const stopPolling = (): void => {
      if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
        pollTimer = undefined;
      }
    };

    const connect = async (): Promise<void> => {
      if (disposed) return;
      controller = new AbortController();
      setStatus('connecting');
      try {
        const res = await fetch(`${API_URL}/api/v1/fights/${fightId}/stream`, {
          headers: {
            accept: 'text/event-stream',
            ...(getAccessToken() ? { authorization: `Bearer ${getAccessToken()}` } : {}),
            ...(lastSeqRef.current > 0 ? { 'last-event-id': String(lastSeqRef.current) } : {}),
          },
          signal: controller.signal,
        });
        if (!res.ok || !res.body) throw new Error(`SSE ${res.status}`);

        stopPolling();
        failures = 0;
        setStatus('live');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done || disposed) break;
          buffer += decoder.decode(value, { stream: true });

          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf('\n\n');

            let data = '';
            let id = 0;
            for (const line of frame.split('\n')) {
              if (line.startsWith('data:')) data += line.slice(5).trim();
              else if (line.startsWith('id:')) id = Number(line.slice(3).trim());
            }
            if (id && id <= lastSeqRef.current) continue; // evento viejo: se descarta
            if (data) {
              try {
                const event = JSON.parse(data) as StreamEvent;
                if (event.seq) lastSeqRef.current = event.seq;
                handlerRef.current(event);
              } catch {
                /* frame inválido: se ignora */
              }
            }
          }
        }
        throw new Error('stream cerrado');
      } catch (error) {
        if (disposed || (error as Error).name === 'AbortError') return;
        failures += 1;
        if (failures >= MAX_SSE_FAILURES) {
          startPolling();
          return;
        }
        setStatus('connecting');
        reconnectTimer = window.setTimeout(() => void connect(), 1_000 * failures);
      }
    };

    void connect();

    const onOnline = (): void => {
      failures = 0;
      stopPolling();
      void connect();
    };
    window.addEventListener('online', onOnline);

    return () => {
      disposed = true;
      window.removeEventListener('online', onOnline);
      stopPolling();
      if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
      controller?.abort();
    };
  }, [fightId, enabled]);

  return { status, lastSeq: lastSeqRef.current };
}
